import React from "react";
import { Helmet } from "react-helmet-async";
import MainLayout from "../../layouts/MainLayout";

const capabilities = [
  {
    n: "01",
    tag: "Coverage",
    headline: "Consolidated and up to date, all amendments tracked.",
    body:
      "Work from a maintained text instead of piecing together amendments from disconnected sources.",
  },
  {
    n: "02",
    tag: "Search",
    headline: "Search by title or content.",
    body:
      "Start with the act name when you know it, or use content search when you only have a clause or phrase.",
  },
  {
    n: "03",
    tag: "Navigation",
    headline: "Easy section navigation.",
    body:
      "Move quickly between parts, chapters, and sections with less scrolling and fewer context breaks.",
  },
  {
    n: "04",
    tag: "Viewing",
    headline: "Designed for mobile and desktop viewing.",
    body:
      "Read and cross-check provisions clearly on both mobile and desktop screens.",
  },
];

const searchExperience = [
  {
    label: "Titles Mode",
    text: "Search by title or year to find a statute quickly when you already know what you need.",
  },
  {
    label: "Full-Text Mode",
    text: "Switch to full-text to search across the body of all Sri Lankan legislation.",
  },
  {
    label: "Featured and Recent",
    text: "Start from featured acts and reopen recent legislation directly from the search interface.",
  },
];

const Legislations = () => {
  return (
    <MainLayout>
      <Helmet>
        <title>Legislation Library | paralegal.lk</title>
        <meta
          name="description"
          content="A consolidated legislation repository by paralegal.lk with title and full-text search across Sri Lankan legislation."
        />
        <link rel="canonical" href="https://www.paralegal.lk/legislations" />
      </Helmet>

      <article className="legislations-page w-full max-w-[1080px] mx-auto px-6 md:px-12 py-10 md:py-16 text-pl-text-primary">
        <header className="flex flex-wrap justify-between items-baseline gap-y-2 pb-4 mb-10 md:mb-14 border-b border-pl-border font-mono text-10 md:text-11 uppercase tracking-[0.22em] text-pl-text-tertiary">
          <div className="flex items-baseline gap-3">
            <span className="text-pl-accent">§</span>
            <span>lex.paralegal.lk</span>
          </div>
          <span>Statutes, kept current</span>
        </header>

        <div className="entry e-0 mb-16 md:mb-20">
          <h1 className="font-display leading-[1.02] tracking-[-0.015em] text-[42px] sm:text-[56px] md:text-[74px] lg:text-[82px] mb-6">
            <em className="italic text-pl-accent">Consolidated</em> legislation
            <br />
            for Sri Lanka.
          </h1>
          <p className="font-display text-18 md:text-21 leading-[1.52] text-pl-text-secondary max-w-[44ch] mb-8 md:mb-10">
            Search by title or switch to full-text to search across the body of all Sri Lankan legislation.
          </p>
          <a
            href="https://lex.paralegal.lk"
            className="group inline-flex items-center gap-3 px-7 py-4 bg-pl-text-primary text-pl-bg rounded-pl font-mono text-11 md:text-12 uppercase tracking-[0.2em] hover:bg-pl-accent transition-colors duration-200"
          >
            Open Legislations
            <span className="transition-transform duration-200 group-hover:translate-x-1">
              →
            </span>
          </a>
        </div>

        <SectionLabel n="I" label="What it does" />

        <div className="space-y-14 md:space-y-18 mb-20 md:mb-24">
          {capabilities.map((item, index) => (
            <WhatItDoesArticle
              key={item.n}
              {...item}
              revealClass={`cap-reveal cr-${index + 1}`}
            />
          ))}
        </div>

        <SectionLabel n="II" label="Search Experience" />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-20 md:mb-24">
          {searchExperience.map((item, index) => (
            <SearchExperienceCard key={item.label} idx={index + 1} {...item} />
          ))}
        </div>

        <SectionLabel n="III" label="Open Library" />

        <div className="entry e-3 rounded-pl-lg border border-pl-border bg-pl-bg-elevated p-6 md:p-8">
          <h2 className="font-display text-28 md:text-34 leading-[1.15] text-pl-text-primary mb-4">
            Start from the law, not from scattered links.
          </h2>
          <p className="text-15 md:text-16 text-pl-text-secondary leading-[1.75] max-w-[60ch] mb-6">
            Open lex.paralegal.lk and begin with title search or full-text mode, then move directly to the provisions you need.
          </p>
          <a
            href="https://lex.paralegal.lk"
            className="group inline-flex items-center gap-3 px-7 py-4 bg-pl-accent text-white rounded-pl font-mono text-11 md:text-12 uppercase tracking-[0.2em] hover:bg-pl-accent-hover transition-colors duration-200"
          >
            Try Legislations
            <span className="transition-transform duration-200 group-hover:translate-x-1">
              →
            </span>
          </a>
        </div>

        <style>{`
          .legislations-page .entry { opacity: 0; transform: translateY(12px); animation: lg-in .75s cubic-bezier(.2,.7,.2,1) forwards; }
          .legislations-page .e-0 { animation-delay: .04s; }
          .legislations-page .e-3 { animation-delay: .10s; }
          .legislations-page .cap-reveal { opacity: 0; transform: translateY(14px); animation: lg-capIn .85s cubic-bezier(.2,.7,.2,1) forwards; }
          .legislations-page .cr-1 { animation-delay: .12s; }
          .legislations-page .cr-2 { animation-delay: .20s; }
          .legislations-page .cr-3 { animation-delay: .28s; }
          .legislations-page .cr-4 { animation-delay: .36s; }
          .legislations-page .cap-rule { transform: scaleX(.15); transform-origin: left; animation: lg-ruleIn .7s ease-out forwards; }

          @keyframes lg-in {
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes lg-capIn {
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes lg-ruleIn {
            to {
              transform: scaleX(1);
            }
          }

          @media (prefers-reduced-motion: reduce) {
            .legislations-page .entry,
            .legislations-page .cap-reveal,
            .legislations-page .cap-rule {
              animation: none;
              opacity: 1;
              transform: none;
            }
          }
        `}</style>
      </article>
    </MainLayout>
  );
};

const SectionLabel = ({ n, label }) => (
  <div className="relative border-t border-pl-border mt-12 mb-10 md:mb-12">
    <div className="absolute -top-[10px] left-0 bg-pl-bg pr-4 font-mono text-10 md:text-11 uppercase tracking-[0.22em] text-pl-text-tertiary flex items-baseline gap-3">
      <span className="text-pl-accent">§ {n}</span>
      <span>{label}</span>
    </div>
  </div>
);

const WhatItDoesArticle = ({ n, tag, headline, body, revealClass }) => (
  <section
    className={`grid grid-cols-1 md:grid-cols-[140px_1fr] gap-y-3 gap-x-10 md:gap-x-16 items-start ${revealClass}`}
  >
    <div className="font-mono uppercase tracking-[0.22em] pt-1 flex md:block items-baseline gap-3">
      <span className="text-11 text-pl-accent">¶ {n}</span>
      <span className="block text-pl-text-tertiary text-10 md:mt-2">{tag}</span>
    </div>
    <div>
      <span
        className="cap-rule block h-[1px] w-20 bg-pl-accent-muted mb-4"
        aria-hidden="true"
      ></span>
      <h3 className="font-display text-24 md:text-32 leading-[1.18] text-pl-text-primary mb-4 max-w-[30ch] tracking-[-0.005em]">
        {headline}
      </h3>
      <p className="text-15 md:text-16 leading-[1.78] text-pl-text-secondary max-w-[62ch]">
        {body}
      </p>
    </div>
  </section>
);

const SearchExperienceCard = ({ idx, label, text }) => (
  <section className="rounded-pl border border-pl-border bg-pl-bg p-6">
    <p className="font-mono text-10 md:text-11 uppercase tracking-[0.2em] text-pl-text-tertiary mb-3">
      Mode {idx}
    </p>
    <h3 className="font-display text-22 md:text-24 leading-[1.25] text-pl-text-primary mb-3">
      {label}
    </h3>
    <p className="text-15 md:text-16 leading-[1.72] text-pl-text-secondary">{text}</p>
  </section>
);

export default Legislations;
